import axios from 'axios'

/**
 * ATL-2068 — what the support chat tells the merchant when talking to the backend failed.
 *
 * The chat used to put `error.message` on screen, which for a failed request is axios's own
 * English text («Request failed with status code 400»). The merchant can do nothing with it.
 * This maps a failure to a translation key under `support.*`, chosen by what the merchant can do
 * about it: fix the message (empty, file too large, file type), re-attach the file, or try again.
 *
 * The 400 reasons are told apart by the backend's message text, because the backend answers all
 * of them with a plain `BadRequestException(<text>)` and no code
 * (`support.service.ts`: `assertCustomerMessageHasContent`, `assertFileAllowed`). The prefixes
 * below are pinned against that source by `supportErrors.atl2068.test.js`, so a rename there
 * fails CI instead of silently degrading every such reply to the generic text.
 */

/** Thrown by `supportApi` when a picked attachment's bytes cannot be read before upload. */
export class SupportAttachmentReadError extends Error {
  readonly filename: string

  constructor(filename: string) {
    super(`Attachment ${filename} could not be read`)
    this.name = 'SupportAttachmentReadError'
    this.filename = filename
  }
}

export type SupportSendErrorKey =
  | 'support.sendErrorEmpty'
  | 'support.sendErrorFileTooLarge'
  | 'support.sendErrorFileType'
  | 'support.sendErrorFileUnreadable'
  | 'support.sendErrorNetwork'
  | 'support.sendErrorServer'
  | 'support.sendErrorGeneric'

export type SupportLoadErrorKey = 'support.loadErrorNetwork' | 'support.loadError'

/** Backend message texts (prefixes) for the 400s a merchant can act on. */
export const BACKEND_EMPTY_MESSAGE = 'message has neither text nor attachments'
export const BACKEND_FILE_TOO_LARGE_PREFIX = 'file too large:'
export const BACKEND_UNSUPPORTED_TYPE_PREFIX = 'unsupported type:'

/** NestJS error bodies carry `message` as a string, or as a list from the validation pipe. */
const backendMessages = (data: unknown): string[] => {
  if (!data || typeof data !== 'object') return []
  const message = (data as { message?: unknown }).message
  if (typeof message === 'string') return [message]
  if (Array.isArray(message)) return message.filter((m): m is string => typeof m === 'string')
  return []
}

/** No response at all: offline, DNS, refused connection, timeout, CORS on web. */
const isNetworkFailure = (e: unknown): boolean => axios.isAxiosError(e) && !e.response

export const supportSendErrorKey = (e: unknown): SupportSendErrorKey => {
  if (e instanceof SupportAttachmentReadError) return 'support.sendErrorFileUnreadable'
  if (!axios.isAxiosError(e)) return 'support.sendErrorGeneric'
  if (isNetworkFailure(e)) return 'support.sendErrorNetwork'
  const status = e.response?.status ?? 0
  // 413 comes from the upload limit in front of the handler (multer / the platform), never
  // from an empty text message: the only thing big enough to hit it is a file.
  if (status === 413) return 'support.sendErrorFileTooLarge'
  if (status >= 500) return 'support.sendErrorServer'
  if (status === 400) {
    const messages = backendMessages(e.response?.data)
    if (messages.some((m) => m === BACKEND_EMPTY_MESSAGE)) return 'support.sendErrorEmpty'
    if (messages.some((m) => m.startsWith(BACKEND_FILE_TOO_LARGE_PREFIX))) {
      return 'support.sendErrorFileTooLarge'
    }
    if (messages.some((m) => m.startsWith(BACKEND_UNSUPPORTED_TYPE_PREFIX))) {
      return 'support.sendErrorFileType'
    }
  }
  return 'support.sendErrorGeneric'
}

export const supportLoadErrorKey = (e: unknown): SupportLoadErrorKey =>
  isNetworkFailure(e) ? 'support.loadErrorNetwork' : 'support.loadError'
